import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Typography, Row, Col, Badge, Divider } from 'antd';
import {
    PhoneOutlined, MailOutlined, CalendarOutlined,
    StarFilled, ArrowLeftOutlined, EnvironmentOutlined,
    MedicineBoxOutlined, IdcardOutlined, LinkOutlined,
    ArrowRightOutlined,
    UserOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { useLanguage } from '../../../context/LanguageContext';
import { getImg } from '../../../Api/getImg';
import CustomTestimonialCarousel from './TestimonialCarousel';
import { slug } from '../../../utils/store';
import NeedAuthModal from '../../Modals/NeedAuthModal';
import GuestBookingModal from '../../Modals/GuestInformationModal';

const { Title, Text, Paragraph } = Typography;

const getDayName = (dayNumber, t) => {
    const days = [
        t('common.days.sunday'),
        t('common.days.monday'),
        t('common.days.tuesday'),
        t('common.days.wednesday'),
        t('common.days.thursday'),
        t('common.days.friday'),
        t('common.days.saturday')
    ];
    return days[dayNumber] || t('common.days.unknown');
};
const orderedDays = [6, 0, 1, 2, 3, 4, 5];


export default function DoctorDetailsPage() {
    const { t, isRTL } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const doctor = location.state?.doctor;
    const isAuthorized = !!localStorage.getItem(`userTkn-${slug}`) || !!sessionStorage.getItem(`userTkn-${slug}`);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isGuestModalVisible, setIsGuestModalVisible] = useState(false);
    const [pendingRoute, setPendingRoute] = useState(null);
    // // If no doctor data, redirect back
    // if (!doctor) {
    //     React.useEffect(() => {
    //         navigate(`/${slug}/our-doctors`);
    //     }, []);
    //     return null;
    // }

    const workingDays = doctor.clinics[0]?.workingDays || [];
    const hasWorkingDays = workingDays.some(day => day.isWorking && day.availabilityHours.length > 0);

    const handleBookAppointment = () => {
        const clinicId = doctor.clinics[0]?.clinicId;
        if (clinicId) {
            navigate(`/book-appointment?DoctorId=${doctor.doctorId}&ClinicId=${clinicId}`);
        } else {
            navigate(`/book-appointment`);
        }
    };
    const handleProtectedNavigation = (route) => {
        if (!isAuthorized) {
            setPendingRoute(route);
            setIsModalVisible(true);
        } else {
            navigate(route);
        }
    };

    const handleGuestBooking = () => {
        setIsModalVisible(false);
        setIsGuestModalVisible(true);
    };

    const handleGuestInfoSubmit = (guestInfo) => {
        setIsGuestModalVisible(false);
        sessionStorage.setItem(`guestInfo-${slug}`, JSON.stringify(guestInfo));
        sessionStorage.setItem(`isGuestBooking-${slug}`, 'true');
        navigate(pendingRoute || `/book-appointment`);
    };
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            {/* Header Section */}
            <div
                className="relative p-4 sm:p-6 lg:p-12 text-white bg-gold"

            >
                {/* Background Decorations - Hidden on mobile */}
                {/* <div className="absolute top-0 right-0 w-20 h-20 sm:w-32 sm:h-32 lg:w-40 lg:h-40 bg-white/10 rounded-full -translate-y-10 translate-x-10 sm:-translate-y-16 sm:translate-x-16 lg:-translate-y-20 lg:translate-x-20"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 bg-white/10 rounded-full translate-y-8 -translate-x-8 sm:translate-y-12 sm:-translate-x-12 lg:translate-y-16 lg:-translate-x-16"></div> */}

                <div className="container mx-auto relative z-10">
                    {/* Back Button */}
                    <Button
                        type="text"
                        icon={isRTL ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
                        onClick={() => navigate(-1)}
                        className="text-white hover:text-white/80 mb-4 sm:mb-6"
                        size={window.innerWidth < 640 ? "middle" : "large"}
                    >
                        {t('common.back')}
                    </Button>

                    <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 lg:space-x-8">
                        {/* Avatar */}
                        <motion.div
                            className="relative"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className='w-28 h-28 sm:w-36 sm:h-36 lg:w-40 lg:h-40 rounded-full'>
                                {doctor.profilePhoto ? <img
                                    src={getImg(doctor.profilePhoto)}
                                    className='rounded-full w-full h-full object-cover border-2 sm:border-4 border-white shadow-2xl'
                                    alt={doctor.fullName}
                                /> : <span className='bg-white w-ful border flex justify-center items-center text-primary h-full rounded-full text-2xl md:text-6xl'><UserOutlined />
                                </span>}


                            </div>
                            <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-white rounded-full p-1.5 sm:p-2 shadow-lg">
                                <div className="flex items-center space-x-1">
                                    <StarFilled className="text-yellow-400 text-xs sm:text-sm" />
                                    <span className="text-xs sm:text-sm font-bold text-gray-700">{t('doctors.card.rating')}</span>
                                </div>
                            </div>
                        </motion.div>

                        {/* Basic Info */}
                        <motion.div
                            className="flex-1"
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <Title
                                level={window.innerWidth < 640 ? 3 : window.innerWidth < 1024 ? 2 : 1}
                                className="text-white mb-2 sm:mb-3 font-bold"
                            >
                                {t("common.dr")} {doctor.fullName}
                            </Title>
                            <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-white/20 backdrop-blur-sm rounded-full mb-3 sm:mb-4">
                                <MedicineBoxOutlined className="mx-2 text-sm sm:text-base" />
                                <span className="font-medium text-sm sm:text-base">{doctor.specialization}</span>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start space-y-2 sm:space-y-0 sm:space-x-4 mt-3 sm:mt-4">
                                <Badge
                                    status="processing"
                                    color='white'
                                    text={hasWorkingDays ? t('doctors.card.available') : t('doctors.card.unavailable')}
                                    className="text-white text-sm"
                                />
                                <span className="text-white/80 hidden sm:inline">•</span>
                                <span className="text-white/80 text-sm">{t('doctors.modal.licensedProfessional')}</span>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-12">
                <Row gutter={[16, 16]} className="sm:gutter-24 lg:gutter-32">
                    {/* Left Column */}
                    <Col xs={24} lg={12}>
                        {/* Contact Information */}
                     

                        {/* Professional Details */}
                        <motion.div
                            className="mb-6 sm:mb-8 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                        >
                            <Title level={window.innerWidth < 640 ? 5 : 4} className="flex items-center mb-3 sm:mb-4 text-gray-800">
                                {t('doctors.modal.professionalDetails')}
                            </Title>

                            <div className="space-y-3 sm:space-y-4">
                                <div className="p-3 sm:p-4 bg-gradient-to-r from-primary/10 to-[#FEBA17]/10 rounded-lg sm:rounded-xl">
                                    <Text className="text-xs sm:text-sm text-gray-600 block mb-1">{t('doctors.modal.specialization')}</Text>
                                    <Text className="font-bold text-primary_hover text-base sm:text-lg">{doctor.specialization}</Text>
                                </div>


                            </div>
                        </motion.div>
                    </Col>

                    {/* Right Column */}
                    <Col xs={24} lg={12}>
                        {/* Clinic Information */}
                        {doctor.clinics[0] && (
                            <motion.div
                                className="mb-6 sm:mb-8 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.5 }}
                            >
                                <Title level={window.innerWidth < 640 ? 5 : 4} className="flex items-center mb-3 sm:mb-4 text-gray-800">
                                    <EnvironmentOutlined className="mx-2 text-primary text-base sm:text-lg" />
                                    {t('doctors.modal.clinicInformation')}
                                </Title>

                                <div className="p-4 sm:p-6 bg-cardColor  rounded-lg sm:rounded-xl border border-primary/20">
                                    <Title level={5} className="text-primary mb-2 sm:mb-3 text-sm sm:text-base">
                                        {doctor.clinics[0].clinicName}
                                    </Title>

                                    <Paragraph className="text-gray-600 mb-3 sm:mb-4 text-xs sm:text-sm">
                                        {doctor.clinics[0].detailedAddress}
                                    </Paragraph>

                                    <div className="flex items-center space-x-2 text-xs sm:text-sm text-green-600">
                                        <div className="w-2 h-2 mx-1  bg-green-500 rounded-full animate-pulse"></div>
                                        <span>{t('doctors.modal.currentlyOperating')}</span>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Working Schedule */}
                        <motion.div
                            className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.6 }}
                        >
                            <Title level={window.innerWidth < 640 ? 5 : 4} className="flex items-center mb-3 sm:mb-4 text-gray-800">
                                <CalendarOutlined className="mx-2 text-primary text-base sm:text-lg" />
                                {t('doctors.modal.workingSchedule')}
                            </Title>
                            {workingDays.length > 0 ? (
                                <div className="space-y-2 sm:space-y-3">
                                    {workingDays
                                        .sort(
                                            (a, b) =>
                                                orderedDays.indexOf(a.dayOfWeek) -
                                                orderedDays.indexOf(b.dayOfWeek)
                                        )
                                        .map((day, index) => (
                                            <div
                                                key={index}
                                                className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 ${day.isWorking && day.availabilityHours.length > 0
                                                        ? 'bg-green-50 border-green-200'
                                                        : 'bg-gray-50 border-gray-200'
                                                    }`}
                                            >
                                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                                                    <div className="flex items-center gap-3 sm:gap-4">
                                                        <span className="font-semibold text-gray-800 w-20 sm:w-28 text-sm sm:text-base">
                                                            {getDayName(day.dayOfWeek, t)}
                                                        </span>
                                                        {day.isWorking && day.availabilityHours.length > 0 ? (
                                                            <Badge status="success" text={t('doctors.modal.available')} className="text-xs sm:text-sm" />
                                                        ) : (
                                                            <Badge status="default" text={t('doctors.modal.closed')} className="text-xs sm:text-sm" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
                                    {t('doctors.modal.noAvailability')}
                                </div>
                            )}
                        </motion.div>
                    </Col>
                </Row>

                <Divider className="my-8 sm:my-12" />
                <CustomTestimonialCarousel doctor={doctor} />
                <Divider className="my-8 sm:my-12" />

                {/* Action Buttons */}
                <motion.div
                    className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                >
                    {hasWorkingDays && (
                        <Button
                            type=""
                            size={window.innerWidth < 640 ? "middle" : "large"}
                            className="h-12 sm:h-14 px-6 sm:px-8 font-bold text-sm text-white sm:text-base bg-goldy rounded-xl shadow-lg"
                            onClick={() => { handleProtectedNavigation(`/book-appointment?DoctorId=${doctor.doctorId}&ClinicId=${doctor.clinics[0]?.clinicId}`) }}
                        >
                            <CalendarOutlined className="mr-2" />
                            {t('doctors.modal.bookAppointment')}
                        </Button>
                    )}
                    {/* <Button
                    type=""
                        size={window.innerWidth < 640 ? "middle" : "large"}
                        className="h-12 sm:h-14 px-6 sm:px-8 font-semibold text-sm sm:text-base rounded-xl border-2 border-primary text-primary hover:bg-primary hover:text-white"
                        onClick={() => window.open(`https://dreasey.com/${doctor?.slug}`, '_blank')}
                    >
                        <LinkOutlined className="mr-2" />
                        {t('doctors.modal.visitDoctorSite')}
                    </Button> */}
                </motion.div>
            </div>
            <NeedAuthModal
                open={isModalVisible}
                onConfirm={() => { setIsModalVisible(false); }}
                onCancel={() => { setIsModalVisible(false); setPendingRoute(null) }}
                onGuestBooking={handleGuestBooking}

            />
            <GuestBookingModal
                open={isGuestModalVisible}
                onCancel={() => { setIsGuestModalVisible(false); setPendingRoute(null); }}
                onSubmit={handleGuestInfoSubmit}
            />
        </div>
    );
}